import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, CalendarDays, GripVertical, List, PieChart } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { modalOverlayClass, segmentedControlClass, segmentedOptionClass } from '../themeHelpers';
import { useSettings } from '../../../contexts/SettingsContext';
import {
  useDashboardLayout,
  useDashboardLayouts,
  useUpdateDashboardLayout,
  useSetDefaultLayout,
  useDeleteDashboardLayout,
  type WidgetConfig,
  type DashboardGroup,
} from '../hooks/useDashboardLayout';
import {
  getVisibleWidgetDefinitions,
  getWidgetDefinition,
  type WidgetCategory,
  type WidgetDefinition,
} from '../widgetRegistry';
import WidgetConfigPanel from './WidgetConfigPanel';
import { Button, Label, TextInput, Select, Badge, ConfirmationDialog } from '../../../components/ui';
import { XIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';

export interface DashboardSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  layoutName?: string;
  editingWidgetId: string | null;
  onClearEditingWidget: () => void;
}

type SettingsTab = 'widgets' | 'groups';

function categoryMeta(
  colors: ReturnType<typeof useTheme>['colors'],
): Record<WidgetCategory, { label: string; color: string; icon: React.ReactNode }> {
  return {
    stats: {
      label: 'Stats',
      color: colors.info,
      icon: <BarChart3 className="h-4 w-4" aria-hidden />,
    },
    lists: {
      label: 'Lists',
      color: colors.success,
      icon: <List className="h-4 w-4" aria-hidden />,
    },
    charts: {
      label: 'Charts',
      color: colors.info,
      icon: <PieChart className="h-4 w-4" aria-hidden />,
    },
    calendar: {
      label: 'Calendar',
      color: colors.warning,
      icon: <CalendarDays className="h-4 w-4" aria-hidden />,
    },
  };
}

const DragHandle = ({ className }: { className?: string }) => (
  <GripVertical className={cn('h-4 w-4 shrink-0', className)} aria-hidden />
);

const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  isOpen,
  onClose,
  layoutName,
  editingWidgetId,
  onClearEditingWidget,
}) => {
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const dashboardGrid = appConfig.dashboard;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedLayoutName, setSelectedLayoutName] = useState<string | undefined>(undefined);
  const effectiveLayoutName = selectedLayoutName ?? layoutName;
  const { layout, mutate: mutateCurrentLayout } = useDashboardLayout(effectiveLayoutName);
  const { layouts, mutate: mutateLayouts } = useDashboardLayouts();
  const [activeTab, setActiveTab] = useState<SettingsTab>('widgets');
  const [widgetSearch, setWidgetSearch] = useState('');

  useEffect(() => {
    if (isOpen) setSelectedLayoutName(layoutName ?? undefined);
  }, [isOpen, layoutName]);

  useEffect(() => {
    if (effectiveLayoutName) mutateCurrentLayout();
  }, [effectiveLayoutName, mutateCurrentLayout]);

  const updateLayoutBase = useUpdateDashboardLayout();
  const updateLayout = useCallback(
    async (layoutId: number, payload: { name?: string; isDefault?: boolean; widgets?: WidgetConfig[]; groups?: DashboardGroup[] }) => {
      await updateLayoutBase(layoutId, payload);
      mutateLayouts();
      mutateCurrentLayout();
    },
    [updateLayoutBase, mutateLayouts, mutateCurrentLayout]
  );
  const setDefaultLayout = useSetDefaultLayout();
  const deleteLayout = useDeleteDashboardLayout();
  const [configPanelWidget, setConfigPanelWidget] = useState<{ id: string; type: string; settings: Record<string, unknown> } | null>(null);
  const [editingLayoutId, setEditingLayoutId] = useState<number | null>(null);
  const [editingLayoutName, setEditingLayoutName] = useState('');

  const defaultWidthByCategory = useMemo(
    () => ({
      stats: dashboardGrid.statCardWidth,
      lists: dashboardGrid.listWidgetWidth,
      charts: dashboardGrid.chartWidth,
      calendar: dashboardGrid.listWidgetWidth,
    }),
    [dashboardGrid]
  );

  const layoutWidgets = layout?.widgets;
  const widgets: WidgetConfig[] = Array.isArray(layoutWidgets) ? layoutWidgets : [];
  const sortedWidgets = useMemo(
    () => [...widgets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [widgets]
  );
  const groups: DashboardGroup[] = layout && Array.isArray(layout.groups) ? layout.groups : [];
  const availableDefinitions = useMemo(() => getVisibleWidgetDefinitions(), []);
  const usedWidgetTypes = useMemo(() => new Set(widgets.map((w) => w.widgetType)), [widgets]);
  const availableToAdd = useMemo(
    () => availableDefinitions.filter((d) => !usedWidgetTypes.has(d.id)),
    [availableDefinitions, usedWidgetTypes]
  );

  const [addWidgetCategory, setAddWidgetCategory] = useState<WidgetCategory | ''>('');
  const availableToAddFiltered = useMemo(() => {
    const q = widgetSearch.trim().toLowerCase();
    return availableToAdd.filter((d) => {
      const matchesCategory = !addWidgetCategory || d.category === addWidgetCategory;
      const matchesSearch =
        !q ||
        d.name.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [availableToAdd, addWidgetCategory, widgetSearch]);

  const categoriesInAvailable = useMemo(
    () => Array.from(new Set(availableToAdd.map((d) => d.category))).sort(),
    [availableToAdd]
  );

  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupTitle, setEditingGroupTitle] = useState('');
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ layoutId: number; name: string } | null>(null);

  const handleReorderWidgets = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const withoutDragged = sortedWidgets.filter((w) => w.id !== draggedId);
      const targetIndex = withoutDragged.findIndex((w) => w.id === targetId);
      const dragged = widgets.find((w) => w.id === draggedId);
      if (!dragged) return;
      const insertAt = targetIndex >= 0 ? targetIndex : withoutDragged.length;
      const reordered = [...withoutDragged.slice(0, insertAt), dragged, ...withoutDragged.slice(insertAt)];
      const updated = reordered.map((w, i) => ({ ...w, order: i }));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, sortedWidgets, widgets, updateLayout, mutateLayouts]
  );

  const handleAddWidget = useCallback(
    (widgetType: string) => {
      const def = getWidgetDefinition(widgetType);
      if (!def || !layout?.id) return;
      const id = `w-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const w = defaultWidthByCategory[def.category] ?? def.defaultSize.w;
      const h = def.defaultSize.h;
      const newWidget: WidgetConfig = {
        id,
        widgetType,
        position: { x: 0, y: widgets.length + 10 },
        size: { w, h },
        settings: { ...def.defaultSettings },
        order: widgets.length,
      };
      const updated = [...widgets, newWidget];
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, defaultWidthByCategory]
  );

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      if (!layout?.id) return;
      const updated = widgets.filter((w) => w.id !== widgetId);
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      if (editingWidgetId === widgetId) onClearEditingWidget();
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, editingWidgetId, onClearEditingWidget]
  );

  const handleOpenConfig = useCallback((w: WidgetConfig) => {
    setConfigPanelWidget({ id: w.id, type: w.widgetType, settings: w.settings ?? {} });
  }, []);

  const handleSaveWidgetSettings = useCallback(
    (settings: Record<string, unknown>) => {
      if (!configPanelWidget || !layout?.id) return;
      const updated = widgets.map((w) =>
        w.id === configPanelWidget.id ? { ...w, settings } : w
      );
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      setConfigPanelWidget(null);
    },
    [configPanelWidget, layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleAddGroup = useCallback(() => {
    if (!layout?.id || !newGroupTitle.trim()) return;
    const id = `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const next: DashboardGroup[] = [...groups, { id, title: newGroupTitle.trim() }];
    updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
    setNewGroupTitle('');
  }, [layout?.id, groups, newGroupTitle, updateLayout, mutateLayouts]);

  const handleRenameGroup = useCallback(
    (groupId: string, title: string) => {
      if (!layout?.id || !title.trim()) return;
      const next = groups.map((g) => (g.id === groupId ? { ...g, title: title.trim() } : g));
      updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
      setEditingGroupTitle('');
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      if (!layout?.id) return;
      const nextGroups = groups.filter((g) => g.id !== groupId);
      const updatedWidgets = widgets.map((w) => (w.groupId === groupId ? { ...w, groupId: undefined } : w));
      updateLayout(layout.id, { groups: nextGroups, widgets: updatedWidgets }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
    },
    [layout?.id, groups, widgets, updateLayout, mutateLayouts]
  );

  const handleSetWidgetGroup = useCallback(
    (widgetId: string, groupId: string | undefined) => {
      if (!layout?.id) return;
      const updated = widgets.map((w) => (w.id === widgetId ? { ...w, groupId: groupId || undefined } : w));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleReorderGroups = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const newOrder = [...groups];
      const from = newOrder.findIndex((x) => x.id === draggedId);
      const to = newOrder.findIndex((x) => x.id === targetId);
      if (from === -1 || to === -1) return;
      const [removed] = newOrder.splice(from, 1);
      newOrder.splice(to, 0, removed);
      updateLayout(layout.id, { groups: newOrder }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleSetDefault = useCallback(
    (layoutId: number) => {
      setDefaultLayout(layoutId).then(() => mutateLayouts()).catch(() => {});
      setEditingLayoutId(null);
    },
    [setDefaultLayout, mutateLayouts]
  );

  const handleRenameSubmit = useCallback(
    (layoutId: number) => {
      const trimmed = editingLayoutName.trim();
      if (!trimmed) return;
      updateLayout(layoutId, { name: trimmed }).then(() => {
        mutateLayouts();
        setEditingLayoutId(null);
        setEditingLayoutName('');
      }).catch(() => {});
    },
    [editingLayoutName, updateLayout, mutateLayouts]
  );

  const handleDeleteClick = useCallback((layoutId: number, name: string) => {
    setDeleteConfirm({ layoutId, name });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const payload = deleteConfirm;
    if (!payload) return;
    setDeleteConfirm(null);
    const { layoutId } = payload;
    const isCurrent = layout?.id === layoutId || (layoutName && layouts.find((l) => l.id === layoutId)?.name === layoutName);
    deleteLayout(layoutId).then(() => {
      mutateLayouts();
      if (isCurrent) {
        const next = new URLSearchParams(searchParams);
        next.delete('dashboard');
        navigate({ pathname: '/dashboard', search: next.toString() });
      }
      if (editingLayoutId === layoutId) setEditingLayoutId(null);
    }).catch(() => {});
  }, [deleteConfirm, layout?.id, layoutName, layouts, deleteLayout, mutateLayouts, navigate, searchParams, editingLayoutId]);

  const displayLayouts = layouts.length > 0 ? layouts : layout ? [layout] : [];

  if (!isOpen) return null;

  return (
    <>
      <div className={`${modalOverlayClass()} items-end sm:items-center p-0 sm:p-4`}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
        <div
          className={cn(
            'relative z-50 w-full flex flex-col overflow-hidden shadow-2xl',
            'max-h-[94dvh] sm:max-h-[88vh] rounded-t-2xl sm:rounded-2xl pb-[env(safe-area-inset-bottom)] sm:pb-0',
            colors.surface,
            colors.border,
            'border sm:max-w-5xl'
          )}
          style={{ minHeight: 'min(480px, 94dvh)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-settings-title"
        >
          {/* Header */}
          <div className={cn('flex-shrink-0 px-4 sm:px-6 py-4 border-b flex items-center justify-between gap-4', colors.border, colors.headerBg)}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', colors.primary, colors.primaryText)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 id="dashboard-settings-title" className={cn('text-lg font-semibold truncate', colors.text)}>
                  Dashboard settings
                </h2>
                <p className={cn('text-xs truncate', colors.textMuted)}>
                  {layout?.name ?? 'Default'}
                  <span className="mx-1.5 opacity-40">·</span>
                  {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
                  {groups.length > 0 && (
                    <>
                      <span className="mx-1.5 opacity-40">·</span>
                      {groups.length} group{groups.length !== 1 ? 's' : ''}
                    </>
                  )}
                </p>
              </div>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <XIcon className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
            {/* Sidebar — dashboard list */}
            <aside className={cn('flex-shrink-0 border-b lg:border-b-0 lg:border-r lg:w-60 xl:w-64 flex flex-col', colors.border, colors.headerBg)}>
              <div className="px-4 py-3">
                <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Dashboards</p>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-1 max-h-36 lg:max-h-none">
                {displayLayouts.length === 0 ? (
                  <p className={cn('px-2 py-3 text-sm', colors.textMuted)}>No dashboards yet.</p>
                ) : (
                  displayLayouts.map((l) => {
                    const isSelected = (effectiveLayoutName ?? layout?.name) === l.name;
                    return (
                      <div
                        key={l.id}
                        className={cn(
                          'rounded-xl border transition-all',
                          isSelected ? cn(colors.primaryBorderAll, colors.surface, 'shadow-sm') : cn(colors.border, 'border-transparent', colors.interactiveHover)
                        )}
                      >
                        {editingLayoutId === l.id ? (
                          <div className="p-2 space-y-2">
                            <TextInput
                              value={editingLayoutName}
                              onChange={(e) => setEditingLayoutName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit(l.id)}
                              className="w-full text-sm"
                              showClearButton={false}
                              autoFocus
                            />
                            <div className="flex gap-1">
                              <Button type="button" size="sm" className="flex-1" onClick={() => handleRenameSubmit(l.id)}>
                                Save
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => { setEditingLayoutId(null); setEditingLayoutName(''); }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setSelectedLayoutName(l.name)}
                            className="w-full text-left px-3 py-2.5 flex items-start gap-2"
                          >
                            <span className={cn('mt-0.5 w-2 h-2 rounded-full shrink-0', isSelected ? colors.primary : cn('border', colors.border))} />
                            <span className="flex-1 min-w-0">
                              <span className={cn('block text-sm font-medium truncate', isSelected ? colors.primaryFg : colors.text)}>{l.name}</span>
                              {l.isDefault && (
                                <Badge variant="accent" className="mt-1 text-[10px] px-1.5 py-0">
                                  Default
                                </Badge>
                              )}
                            </span>
                          </button>
                        )}
                        {editingLayoutId !== l.id && isSelected && (
                          <div className={cn('flex items-center gap-0.5 px-2 pb-2 border-t pt-2 mx-2', colors.borderLight)}>
                            {!l.isDefault && (
                              <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={() => handleSetDefault(l.id)}>
                                Set default
                              </Button>
                            )}
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={() => { setEditingLayoutId(l.id); setEditingLayoutName(l.name); }} title="Rename" aria-label="Rename">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </Button>
                            <Button type="button" variant="ghost" size="icon" className={cn('h-7 w-7', colors.error, 'bg-transparent')} onClick={() => handleDeleteClick(l.id, l.name)} title="Delete" aria-label="Delete">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              {configPanelWidget ? (
                <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
                  <button
                    type="button"
                    onClick={() => setConfigPanelWidget(null)}
                    className={cn('inline-flex items-center gap-1.5 text-sm font-medium mb-4 transition-colors hover:opacity-80', colors.primaryFg)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to widgets
                  </button>
                  <WidgetConfigPanel
                    widgetId={configPanelWidget.id}
                    widgetType={configPanelWidget.type}
                    settings={configPanelWidget.settings}
                    onSave={handleSaveWidgetSettings}
                    onClose={() => setConfigPanelWidget(null)}
                  />
                </div>
              ) : (
                <>
                  {/* Tabs */}
                  <div className={cn('flex-shrink-0 px-4 sm:px-6 pt-4 pb-0 border-b', colors.border)}>
                    <div className={segmentedControlClass(colors)}>
                      <button
                        type="button"
                        onClick={() => setActiveTab('widgets')}
                        className={segmentedOptionClass(colors, activeTab === 'widgets', 'soft')}
                      >
                        Widgets
                        <span className={cn('ml-1 text-xs opacity-70', activeTab === 'widgets' ? '' : colors.textMuted)}>{widgets.length}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('groups')}
                        className={segmentedOptionClass(colors, activeTab === 'groups', 'soft')}
                      >
                        Groups
                        <span className={cn('ml-1 text-xs opacity-70', activeTab === 'groups' ? '' : colors.textMuted)}>{groups.length}</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
                    {activeTab === 'widgets' ? (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 xl:gap-6 items-start">
                        {/* Widget catalog */}
                        <section className="min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                              <h3 className={cn('text-sm font-semibold', colors.text)}>Add widgets</h3>
                              <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                                {availableToAdd.length} available · {availableDefinitions.length} total types
                              </p>
                            </div>
                          </div>

                          <TextInput
                            value={widgetSearch}
                            onChange={(e) => setWidgetSearch(e.target.value)}
                            placeholder="Search widgets…"
                            className="mb-3"
                            showClearButton={!!widgetSearch}
                          />

                          {categoriesInAvailable.length > 1 && (
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              <button
                                type="button"
                                onClick={() => setAddWidgetCategory('')}
                                className={cn(
                                  'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                                  !addWidgetCategory ? cn(colors.primary, colors.primaryText) : cn(colors.surfaceHover, colors.textSecondary)
                                )}
                              >
                                All
                              </button>
                              {categoriesInAvailable.map((cat) => {
                                const meta = categoryMeta(colors)[cat];
                                return (
                                  <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setAddWidgetCategory(cat)}
                                    className={cn(
                                      'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                                      addWidgetCategory === cat ? cn(colors.primary, colors.primaryText) : cn(colors.surfaceHover, colors.textSecondary)
                                    )}
                                  >
                                    {meta.icon}
                                    {meta.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {availableToAdd.length === 0 ? (
                            <EmptyPanel colors={colors} message="All widgets are on this dashboard." />
                          ) : availableToAddFiltered.length === 0 ? (
                            <EmptyPanel colors={colors} message="No widgets match your search." />
                          ) : (
                            <div className="space-y-2">
                              {availableToAddFiltered.map((d) => (
                                <WidgetCatalogItem key={d.id} def={d} colors={colors} onAdd={() => handleAddWidget(d.id)} />
                              ))}
                            </div>
                          )}
                        </section>

                        {/* Active widgets */}
                        <section className="min-w-0">
                          <div className="mb-3">
                            <h3 className={cn('text-sm font-semibold', colors.text)}>On this dashboard</h3>
                            <p className={cn('text-xs mt-0.5', colors.textMuted)}>Drag to reorder · click gear to configure</p>
                          </div>

                          {sortedWidgets.length === 0 ? (
                            <EmptyPanel
                              colors={colors}
                              message="No widgets yet. Pick one from the catalog."
                              icon={
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4z" />
                                </svg>
                              }
                            />
                          ) : (
                            <ul className="space-y-2">
                              {sortedWidgets.map((w) => {
                                const def = getWidgetDefinition(w.widgetType);
                                const meta = def ? categoryMeta(colors)[def.category] : null;
                                return (
                                  <li
                                    key={w.id}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('text/plain', w.id);
                                      e.dataTransfer.effectAllowed = 'move';
                                      setDraggedWidgetId(w.id);
                                    }}
                                    onDragEnd={() => setDraggedWidgetId(null)}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const draggedId = e.dataTransfer.getData('text/plain');
                                      if (draggedId && draggedId !== w.id) handleReorderWidgets(draggedId, w.id);
                                      setDraggedWidgetId(null);
                                    }}
                                    className={cn(
                                      'group flex items-center gap-2 p-3 rounded-xl border transition-all cursor-grab active:cursor-grabbing',
                                      colors.border,
                                      colors.surface,
                                      draggedWidgetId === w.id && 'opacity-50 scale-[0.98]'
                                    )}
                                  >
                                    <DragHandle className={colors.textMuted} />
                                    {meta && (
                                      <span className={cn('p-1.5 rounded-lg shrink-0', meta.color)}>{meta.icon}</span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <span className={cn('block text-sm font-medium truncate', colors.text)}>
                                        {def?.name ?? w.widgetType}
                                      </span>
                                      {def && (
                                        <span className={cn('block text-[11px] truncate', colors.textMuted)}>{def.description}</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                      {groups.length > 0 && (
                                        <Select
                                          value={w.groupId ?? ''}
                                          onChange={(e) => handleSetWidgetGroup(w.id, (e.target.value as string) || undefined)}
                                          options={[{ value: '', label: 'Ungrouped' }, ...groups.map((g) => ({ value: g.id, label: g.title }))]}
                                          showClearButton={false}
                                          className="w-28 text-xs h-8"
                                        />
                                      )}
                                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenConfig(w)} title="Configure" aria-label="Configure">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                      </Button>
                                      <Button type="button" variant="ghost" size="icon" className={cn('h-8 w-8', colors.error, 'bg-transparent')} onClick={() => handleRemoveWidget(w.id)} title="Remove" aria-label="Remove">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      </Button>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </section>
                      </div>
                    ) : (
                      <section className="max-w-xl">
                        <div className="mb-4">
                          <h3 className={cn('text-sm font-semibold', colors.text)}>Section groups</h3>
                          <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                            Organize widgets into labeled sections on the dashboard (e.g. Today, Email).
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 mb-5">
                          <TextInput
                            value={newGroupTitle}
                            onChange={(e) => setNewGroupTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
                            placeholder="Group name, e.g. Today"
                            className="flex-1"
                            showClearButton={false}
                          />
                          <Button type="button" onClick={handleAddGroup} disabled={!newGroupTitle.trim()} className="shrink-0">
                            Add group
                          </Button>
                        </div>

                        {groups.length === 0 ? (
                          <EmptyPanel
                            colors={colors}
                            message="No groups yet. Groups create section headings on your dashboard."
                            icon={
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                            }
                          />
                        ) : (
                          <ul className="space-y-2">
                            {groups.map((g) => (
                              <li
                                key={g.id}
                                draggable={editingGroupId !== g.id}
                                onDragStart={(e) => {
                                  if (editingGroupId === g.id) return;
                                  e.dataTransfer.setData('text/plain', g.id);
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDraggedGroupId(g.id);
                                }}
                                onDragEnd={() => setDraggedGroupId(null)}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const draggedId = e.dataTransfer.getData('text/plain');
                                  if (draggedId && draggedId !== g.id) handleReorderGroups(draggedId, g.id);
                                  setDraggedGroupId(null);
                                }}
                                className={cn(
                                  'flex items-center gap-2 p-3 rounded-xl border transition-all',
                                  colors.border,
                                  colors.surface,
                                  editingGroupId !== g.id && 'cursor-grab active:cursor-grabbing',
                                  draggedGroupId === g.id && 'opacity-50'
                                )}
                              >
                                {editingGroupId === g.id ? (
                                  <>
                                    <TextInput
                                      value={editingGroupTitle}
                                      onChange={(e) => setEditingGroupTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameGroup(g.id, editingGroupTitle);
                                        if (e.key === 'Escape') setEditingGroupId(null);
                                      }}
                                      className="flex-1"
                                      showClearButton={false}
                                      autoFocus
                                    />
                                    <Button type="button" size="sm" onClick={() => handleRenameGroup(g.id, editingGroupTitle)}>Save</Button>
                                    <Button type="button" variant="outline" size="sm" onClick={() => setEditingGroupId(null)}>Cancel</Button>
                                  </>
                                ) : (
                                  <>
                                    <DragHandle className={colors.textMuted} />
                                    <span className={cn('flex-1 text-sm font-medium truncate', colors.text)}>{g.title}</span>
                                    <span className={cn('text-xs shrink-0', colors.textMuted)}>
                                      {widgets.filter((w) => w.groupId === g.id).length} widget{widgets.filter((w) => w.groupId === g.id).length !== 1 ? 's' : ''}
                                    </span>
                                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingGroupId(g.id); setEditingGroupTitle(g.title); }} title="Rename">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    </Button>
                                    <Button type="button" variant="ghost" size="icon" className={cn('h-8 w-8', colors.error, 'bg-transparent')} onClick={() => handleRemoveGroup(g.id)} title="Remove">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </Button>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete dashboard"
        message={deleteConfirm ? `Delete dashboard "${deleteConfirm.name}"? This cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

function EmptyPanel({
  colors,
  message,
  icon,
}: {
  colors: ReturnType<typeof useTheme>['colors'];
  message: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border border-dashed px-4 py-10 text-center', colors.border, colors.surfaceHover)}>
      {icon && <div className={cn('mx-auto mb-3 opacity-40', colors.textMuted)}>{icon}</div>}
      <p className={cn('text-sm', colors.textMuted)}>{message}</p>
    </div>
  );
}

function WidgetCatalogItem({
  def,
  colors,
  onAdd,
}: {
  def: WidgetDefinition;
  colors: ReturnType<typeof useTheme>['colors'];
  onAdd: () => void;
}) {
  const meta = categoryMeta(colors)[def.category];
  return (
    <button
      type="button"
      onClick={onAdd}
      className={cn(
        'group w-full rounded-xl border p-3 text-left transition-all',
        colors.border,
        colors.surface,
        colors.interactiveHover,
        colors.hoverBorderPrimary,
      )}
    >
      <div className="flex items-start gap-3">
        <span className={cn('p-2 rounded-lg shrink-0 transition-transform group-hover:scale-105', meta.color)}>
          {meta.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-sm font-medium', colors.text)}>{def.name}</span>
            <span className={cn('text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded', meta.color)}>
              {meta.label}
            </span>
          </div>
          <p className={cn('text-xs mt-0.5 line-clamp-2', colors.textMuted)}>{def.description}</p>
        </div>
        <span className={cn('shrink-0 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity', colors.primary, colors.primaryText)}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </div>
    </button>
  );
}

export default DashboardSettings;
